import type { AssetDetail, ChangeTarget, ContextVersion, VersionDetail, VersionState } from '@lastrouter/contracts';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Check,
  Dialog,
  DialogContent,
  Field,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  X,
} from '@lastrouter/ui';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { EmptyState, QueryState, StatusBadge } from '../../components/common.js';
import { formatDateTime } from '../../lib/format.js';

/** O que uma mudança no contexto do app recarrega na tela. */
export const ASSET_QUERIES = ['asset', 'assetVersions', 'assetChanges', 'assets', 'searchAssets', 'clients'] as const;

const STATE_TONE = { rascunho: 'warning', publicada: 'success', arquivada: 'neutral', descartada: 'neutral' } as const;

export function VersionStateBadge({ state }: { state: VersionState }) {
  const { t } = useTranslation();
  return <StatusBadge tone={STATE_TONE[state]}>{t(`assets.states.${state}`)}</StatusBadge>;
}

type Papel = 'negocio' | 'tecnico';
/** inPlaceOf: o admin decide no lugar deste dono ausente, com motivo obrigatório. */
type Decision = { papel: Papel; decision: 'aprovado' | 'recusado'; inPlaceOf?: string };

const MIN_REASON = 10;

function DecisionDialog({ draft, appName, d, onClose }: { draft: VersionDetail; appName: string; d: Decision; onClose: () => void }) {
  const { t } = useTranslation();
  const approve = useApiMutation('approveVersion', [...ASSET_QUERIES]);
  const [comment, setComment] = useState('');
  const verb = d.decision === 'aprovado' ? t('context.editor.approve') : t('context.editor.refuse');
  const substitute = Boolean(d.inPlaceOf);

  function submit(e: FormEvent) {
    e.preventDefault();
    approve.mutate(
      { params: { id: draft.version.id }, body: { papel: d.papel, decision: d.decision, comment: comment.trim() || null, no_lugar_do_dono: substitute, expected_updated_at: draft.version.updated_at } },
      { onSuccess: onClose, onError: (err) => toast.error(errorMessage(t, err)) },
    );
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={t('context.editor.decisionTitle', {
          decision: verb,
          papel: d.papel === 'negocio' ? t('context.editor.papelNegocio') : t('context.editor.papelTecnico'),
          subject: appName,
        })}
        closeLabel={t('common.close')}
      >
        <form className="flex flex-col gap-4" onSubmit={submit}>
          {substitute ? <Alert tone="warning" title={t('context.editor.substituteHelp', { owner: d.inPlaceOf })} /> : null}
          <Field
            id="comentario-aprovacao"
            label={substitute ? t('context.editor.reason') : `${t('context.editor.comment')} (${t('common.optional')})`}
          >
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} maxLength={2000} required={substitute} minLength={substitute ? MIN_REASON : undefined} />
          </Field>
          <div className="flex justify-end gap-2">
            <Button onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" variant={d.decision === 'aprovado' ? 'primary' : 'danger'} disabled={approve.isPending || (substitute && comment.trim().length < MIN_REASON)}>
              {verb}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** Aprovações dos donos do rascunho, decidir no lugar do dono ausente e descartar o rascunho. */
export function ApprovalsCard({ draft, appName }: { draft: VersionDetail; appName: string }) {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [discarding, setDiscarding] = useState(false);
  const { version, status, can_approve, can_substitute } = draft;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('assets.approvals', { n: version.number })}</CardTitle>
        <CardDescription>{t('assets.approvalsHelp')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {status.ready ? <Alert tone="success" title={t('context.editor.ready')} /> : null}
        {status.refused ? <Alert tone="danger" title={t('context.editor.refusedState')} /> : null}
        <ul className="flex flex-col gap-2">
          {(['negocio', 'tecnico'] as const).map((papel) => {
            const given = version.approvals.find((a) => a.papel === papel);
            const pending = status.pending.find((p) => p.papel === papel);
            return (
              <li key={papel} className="flex flex-wrap items-center gap-2">
                <span className="w-20 text-fg-secondary">{papel === 'negocio' ? t('context.business') : t('context.technical')}</span>
                {given?.decision === 'aprovado' ? (
                  <StatusBadge tone="success">
                    {given.no_lugar_de ? t('context.editor.approvedInPlace', { by: given.by, owner: given.no_lugar_de }) : t('context.editor.approvedBy', { by: given.by })}
                  </StatusBadge>
                ) : given?.decision === 'recusado' ? (
                  <StatusBadge tone="danger">
                    {given.no_lugar_de ? t('context.editor.refusedInPlace', { by: given.by, owner: given.no_lugar_de }) : t('context.editor.refusedBy', { by: given.by })}
                  </StatusBadge>
                ) : (
                  <StatusBadge tone="warning">{t('context.editor.pending', { owner: pending?.owner ?? '' })}</StatusBadge>
                )}
                {given?.comment ? <span className="text-[length:var(--lr-text-sm)] text-fg-secondary">"{given.comment}"</span> : null}
                {can_approve.includes(papel) ? (
                  <span className="flex gap-1">
                    {/* Dois pares iguais na tela: o nome acessível diz de qual papel é cada botão. */}
                    <Button
                      size="sm"
                      variant="primary"
                      aria-label={`${t('context.editor.approve')}: ${papel === 'negocio' ? t('context.business') : t('context.technical')}`}
                      onClick={() => setDecision({ papel, decision: 'aprovado' })}
                    >
                      <Check />
                      {t('context.editor.approve')}
                    </Button>
                    <Button
                      size="sm"
                      aria-label={`${t('context.editor.refuse')}: ${papel === 'negocio' ? t('context.business') : t('context.technical')}`}
                      onClick={() => setDecision({ papel, decision: 'recusado' })}
                    >
                      <X />
                      {t('context.editor.refuse')}
                    </Button>
                  </span>
                ) : can_substitute.includes(papel) && pending ? (
                  <Button
                    size="sm"
                    aria-label={`${t('context.editor.substitute')}: ${papel === 'negocio' ? t('context.business') : t('context.technical')}`}
                    onClick={() => setDecision({ papel, decision: 'aprovado', inPlaceOf: pending.owner })}
                  >
                    {t('context.editor.substitute')}
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div>
          <Button variant="danger" onClick={() => setDiscarding(true)}>
            {t('assets.discard')}
          </Button>
        </div>
      </CardContent>
      {decision ? <DecisionDialog draft={draft} appName={appName} d={decision} onClose={() => setDecision(null)} /> : null}
      {discarding ? <DiscardDialog draft={draft} onClose={() => setDiscarding(false)} /> : null}
    </Card>
  );
}

function DiscardDialog({ draft, onClose }: { draft: VersionDetail; onClose: () => void }) {
  const { t } = useTranslation();
  const discard = useApiMutation('discardVersion', [...ASSET_QUERIES]);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('assets.discard')} description={t('assets.discardConfirm', { n: draft.version.number })} closeLabel={t('common.close')}>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            disabled={discard.isPending}
            onClick={() =>
              discard.mutate(
                { params: { id: draft.version.id } },
                {
                  onSuccess: () => {
                    toast.success(t('assets.discarded'));
                    onClose();
                  },
                  onError: (err) => toast.error(errorMessage(t, err)),
                },
              )
            }
          >
            {t('assets.discard')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Histórico de versões, com o selo de quem foi publicada sem validação, e abrir rascunho de uma versão antiga. */
export function VersionsPanel({ detail, onOpenFrom }: { detail: AssetDetail; onOpenFrom: (versionId: string, n: number) => void }) {
  const { t } = useTranslation();
  const q = useApiQuery('assetVersions', { params: { id: detail.asset.id } });
  return (
    <QueryState query={q}>
      {(versions) =>
        versions.length === 0 ? (
          <EmptyState>{t('assets.noVersions')}</EmptyState>
        ) : (
          <Table>
            <TableCaption>{t('assets.versions')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('assets.versionsTable.number')}</TableHead>
                <TableHead>{t('assets.versionsTable.state')}</TableHead>
                <TableHead>{t('assets.versionsTable.by')}</TableHead>
                <TableHead>{t('assets.versionsTable.when')}</TableHead>
                <TableHead>{t('assets.versionsTable.reason')}</TableHead>
                <TableHead>
                  <span className="sr-only">{t('common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="tabular font-medium">{t('assets.versionN', { n: v.number })}</TableCell>
                  <TableCell>
                    <span className="flex flex-wrap gap-1">
                      <VersionStateBadge state={v.state} />
                    </span>
                  </TableCell>
                  <TableCell>{v.published_by ?? v.created_by}</TableCell>
                  <TableCell className="tabular">{formatDateTime(v.published_at ?? v.created_at)}</TableCell>
                  <TableCell>
                    {v.reason}
                    {v.unvalidated_publish ? (
                      <p className="mt-1 text-[length:var(--lr-text-sm)] text-fg-secondary">
                        {t('assets.unvalidated.reason')}: {v.unvalidated_publish.justification} ({v.unvalidated_publish.by})
                      </p>
                    ) : null}
                    {v.summary.length ? (
                      <ul className="mt-1 list-disc pl-4 text-[length:var(--lr-text-sm)] text-fg-secondary">
                        {v.summary.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-right">
                    {v.state === 'arquivada' ? (
                      <Button size="sm" disabled={Boolean(detail.draft)} title={detail.draft ? t('assets.draftExists') : undefined} onClick={() => onOpenFrom(v.id, v.number)}>
                        {t('assets.openFrom')}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      }
    </QueryState>
  );
}

/** Alterações do contexto, da mais nova para a mais antiga, com o texto de antes e depois. */
export function HistoryPanel({ assetId }: { assetId: string }) {
  const { t } = useTranslation();
  const q = useApiQuery('assetChanges', { params: { id: assetId } });
  const part = (target: ChangeTarget) => (target === 'semantica' ? t('assets.semantic') : t(`assets.sections.${target}`));
  return (
    <QueryState query={q}>
      {(changes) =>
        changes.length === 0 ? (
          <EmptyState>{t('assets.changes.empty')}</EmptyState>
        ) : (
          <Table>
            <TableCaption>{t('assets.history')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('assets.changes.when')}</TableHead>
                <TableHead>{t('assets.changes.who')}</TableHead>
                <TableHead>{t('assets.changes.part')}</TableHead>
                <TableHead>{t('assets.changes.operation')}</TableHead>
                <TableHead>{t('assets.changes.reason')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {changes.map((c) => (
                <TableRow key={c.id} className="align-top">
                  <TableCell className="tabular">{formatDateTime(c.created_at)}</TableCell>
                  <TableCell>{c.user}</TableCell>
                  <TableCell>{part(c.target)}</TableCell>
                  <TableCell>{t(`assets.operations.${c.operation}`)}</TableCell>
                  <TableCell className="min-w-64">
                    {c.reason ?? '-'}
                    <details className="mt-1">
                      <summary className="cursor-pointer text-[length:var(--lr-text-sm)] text-accent-text">{t('assets.changes.show')}</summary>
                      <div className="mt-2 grid gap-2 lg:grid-cols-2">
                        {(
                          [
                            ['before', c.before_text],
                            ['after', c.after_text],
                          ] as const
                        ).map(([k, text]) => (
                          <div key={k}>
                            <p className="text-[length:var(--lr-text-xs)] font-medium text-fg-secondary">{t(`assets.changes.${k}`)}</p>
                            <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-[var(--lr-radius-md)] bg-sunken p-2 font-mono text-[length:var(--lr-text-xs)]">{text || '-'}</pre>
                          </div>
                        ))}
                      </div>
                    </details>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      }
    </QueryState>
  );
}
